import React from 'react';
import { FormattedMessage } from 'react-intl';
import type { StyledProps } from '@compiled/react';
import { styled } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import { token } from '@atlaskit/tokens';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { CONTENT_STATUS_ARCHIVED } from '@confluence/archive-pages/entry-points/archivingConstants.ts';
import { StarSelectedIcon } from '@confluence/icons/entry-points/StarSelectedIcon.ts';

import { i18n } from '../i18n.js';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const TitleStatus: React.ComponentType<
	React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled,
> = styled.div({
	flexShrink: 0,
	flexGrow: 1,
	textAlign: 'left',
	marginLeft: token('space.050'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginTop: '-1px',
	height: '16px',
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LozengeContainer = styled.div({
	margin: '1px 5px 0 5px',
});

const inlineStarContainerStyles = xcss({
	marginTop: 'space.025',
});

type StatusLozengeProps = {
	status: string;
	unpublished?: boolean;
	scheduled?: boolean;
	showStar?: boolean;
};

export const StatusLozenge = ({
	status,
	unpublished = false,
	scheduled = false,
	showStar = false,
}: StatusLozengeProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	if (scheduled) {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				actionSubject: 'feature',
				action: 'exposed',
				source: 'StatusLozenge',
			},
		}).fire();
	}

	return (
		<TitleStatus>
			{status === 'draft' || status === CONTENT_STATUS_ARCHIVED ? (
				<LozengeContainer>
					<Lozenge appearance="default">
						<FormattedMessage
							{...(status === 'draft' ? i18n.contentDraft : i18n.contentArchived)}
						/>
					</Lozenge>
				</LozengeContainer>
			) : null}
			{(scheduled && unpublished) || (scheduled && !unpublished) ? (
				<LozengeContainer>
					<Lozenge appearance="default" isBold>
						<FormattedMessage {...i18n.contentScheduled} />
					</Lozenge>
				</LozengeContainer>
			) : (
				unpublished && (
					<LozengeContainer>
						<Lozenge appearance="default">
							<FormattedMessage {...i18n.contentUnpublished} />
						</Lozenge>
					</LozengeContainer>
				)
			)}
			{showStar ? (
				<Box xcss={inlineStarContainerStyles}>
					<StarSelectedIcon size="small" label="Favorite" />
				</Box>
			) : null}
		</TitleStatus>
	);
};
